import { Cache } from "../Cache.js";
import { i18n } from "../../globalization/i18n.js";
import { getConfig } from "../../../config.js";
import * as dtv from "../../data/values.js";
import { isString } from "../utils.js";
function getLogger() {

    return {
        warning: (...args) => console.warn(...args),
        warn: (...args) => console.warn(...args),
        error: (...args) => console.error(...args),
        info: (...args) => console.info(...args),
    };

}

// --------------------------------------------------------------------------------------------

const Kinds = {
    Bool: 'bool',
    Ref: 'ref',
    Symbol: 'symbol',
    Duration: 'duration',
    Number: 'number',
    String: 'string',
    Datetime: 'datetime',
};

Kinds.All = Object.keys(Kinds);

// --------------------------------------------------------------------------------------------

const Types = Object.freeze({
    Bool: 'bool',
    Ref: 'ref',
    Int: 'int',
    Float: 'float',
    Decimal: 'decimal',
    String: 'string',
    Text: 'text',
    RichString: 'rich_string',
    RichText: 'rich_text',
    UtcDatetime: 'utc_datetime',
    ZonedDatetime: 'zoned_datetime',
    LocalDatetime: 'local_datetime',
    LocalDate: 'local_date',
    LocalTime: 'local_time',
    Duration: 'duration',
});

const RuntimeTypes = Object.freeze({
    Bool: 'bool',
    Ref: 'ref',
    Int: 'int',
    Float: 'float',
    Decimal: 'decimal',
    String: 'string',
    UtcDatetime: 'utc_datetime',
    ZonedDatetime: 'zoned_datetime',
    LocalDatetime: 'local_datetime',
    LocalDate: 'local_date',
    LocalTime: 'local_time',
    Duration: 'duration',
});

// --------------------------------------------------------------------------------------------

const TypeInfos = [
    {
        id: Types.Bool,
        name: Types.Bool,
        kind: Kinds.Bool,
        label: () => i18n.t('type.bool.label'),
        hint: () => i18n.t('type.bool.hint'),
    },
    {
        id: Types.Ref,
        name: Types.Ref,
        kind: Kinds.Ref,
        label: () => i18n.t('type.ref.label'),
        hint: () => i18n.t('type.ref.hint'),
    },
    {
        id: Types.Int,
        name: Types.Int,
        kind: Kinds.Number,
        label: () => i18n.t('type.int.label'),
        hint: () => i18n.t('type.int.hint'),
    },
    {
        id: Types.Float,
        name: Types.Float,
        kind: Kinds.Number,
        label: () => i18n.t('type.float.label'),
        hint: () => i18n.t('type.float.hint'),
    },
    {
        id: Types.Decimal,
        name: Types.Decimal,
        kind: Kinds.Number,
        label: () => i18n.t('type.decimal.label'),
        hint: () => i18n.t('type.decimal.hint'),
    },
    {
        id: Types.String,
        name: Types.String,
        kind: Kinds.String,
        label: () => i18n.t('type.string.label'),
        hint: () => i18n.t('type.string.hint'),
    },
    {
        id: Types.Text,
        name: Types.Text,
        kind: Kinds.String,
        label: () => i18n.t('type.text.label'),
        hint: () => i18n.t('type.text.hint'),
    },
    {
        id: Types.RichString,
        name: Types.RichString,
        kind: Kinds.String,
        label: () => i18n.t('type.richstring.label'),
        hint: () => i18n.t('type.richstring.hint'),
    },
    {
        id: Types.RichText,
        name: Types.RichText,
        kind: Kinds.String,
        label: () => i18n.t('type.richtext.label'),
        hint: () => i18n.t('type.richtext.hint'),
    },
    {
        id: Types.UtcDatetime,
        name: Types.UtcDatetime,
        kind: Kinds.Datetime,
        label: () => i18n.t('type.utc_datetime.label'),
        hint: () => i18n.t('type.utc_datetime.hint'),
    },
    {
        id: Types.ZonedDatetime,
        name: Types.ZonedDatetime,
        kind: Kinds.Datetime,
        label: () => i18n.t('type.zoned_datetime.label'),
        hint: () => i18n.t('type.zoned_datetime.hint'),
    },
    {
        id: Types.LocalDatetime,
        name: Types.LocalDatetime,
        kind: Kinds.Datetime,
        label: () => i18n.t('type.local_datetime.label'),
        hint: () => i18n.t('type.local_datetime.hint'),
    },
    {
        id: Types.LocalDate,
        name: Types.LocalDate,
        kind: Kinds.Datetime,
        label: () => i18n.t('type.local_date.label'),
        hint: () => i18n.t('type.local_date.hint'),
    },
    {
        id: Types.LocalTime,
        name: Types.LocalTime,
        kind: Kinds.Datetime,
        label: () => i18n.t('type.local_time.label'),
        hint: () => i18n.t('type.local_time.hint'),
    },
    {
        id: Types.Duration,
        name: Types.Duration,
        kind: Kinds.Duration,
        label: () => i18n.t('type.duration.label'),
        hint: () => i18n.t('type.duration.hint'),
    },
];

//TYPES_DEFS.forEach((def) => types.push(Object.assign({}, def)));

const _codelists = new Cache();

// ------------------------------------------------------------------------

let $initialized = false;

function initialize() {

    if ($initialized)
        return;

    // nothing to initialize

    $initialized = true;

};

// ------------------------------------------------------------------------

function getTypes() {

    return TypeInfos;

};

function getTypeByName(name, failIfNotFound = false) {

    let type = getTypes().find((t) => t.name == name);
    if (!type && failIfNotFound)
        throw new Error(`Type with name "${name}" is not supported.`);
    return type;

};

function getTypeById(id, failIfNotFound = false) {

    if (!id)
        return null;
    let type = getTypes().find((t) => t.id == id);
    if (!type && failIfNotFound)
        throw new Error(`Type with id "${id}" is not supported.`);
    return type;

};

// ------------------------------------------------------------------------

function getTypeKind(type) {

    if (!type)
        return null;

    const t = getTypeByName(type, false);

    if (!t)
        return null;

    return t.kind;

}

function getTypesByKind(kind) {

    return getTypes().filter(type => type.kind == kind);

};

function isTypeIdOfKind(typeId, kindId) {

    return (typeId & kindId) != 0;

};

// ------------------------------------------------------------------------

function isOfType(actual, expected) {
    return actual === expected || actual.startsWith(`${expected}:`);
}

function isBoolType(type) { return isOfType(type, Types.Bool); }

function isRefType(type) { return isOfType(type, Types.Ref); }

function isIntType(type) { return isOfType(type, Types.Int); }

function isFloatType(type) { return isOfType(type, Types.Float); }

function isNumericType(type) { return isIntType(type) || isFloatType(type); }

function isStringType(type) { return isOfType(type, Types.String); }

function isUtcDatetimeType(type) { return isOfType(type, Types.UtcDatetime); }

function isZonedDatetimeType(type) { return isOfType(type, Types.ZonedDatetime); }

function isLocalDatetimeType(type) { return isOfType(type, Types.LocalDatetime); }

function isLocalDateType(type) { return isOfType(type, Types.LocalDate); }

function isLocalTimeType(type) { return isOfType(type, Types.LocalTime); }

function isDurationType(type) { return isOfType(type, Types.Duration); }

function isCodelistType(type) {

    if (!type)
        return false;
    return !!type.valuesEndpoint;

};

function isValueType(type) {

    if (!type)
        return false;
    if (isRefType(type))
        return false;
    if (isCodelistType(type))
        return false;
    return true;

}

function getTypeCodelistName(type) {

    if (isString(type))
        type = getTypeByName(type);
    if (!isCodelistType(type))
        return null;
    return type && type.valuesEndpoint;

};

function getTypeValues(type) {

    if (isString(type))
        type = getTypeByName(type);

    if (!isCodelistType(type))
        return [];

    if (!type.valuesEndpoint)
        throw new Error(`Codelist type "${type.name}" is missing values endpoint path.`);

    const data = _codelists.getData(type.valuesEndpoint);

    if (Array.isArray(data))
        return data;

    return [];

};

async function loadTypeValues(type, valueLoader, forceLoad = false) {

    if (isString(type))
        type = getTypeByName(type);

    if (!isCodelistType(type))
        return false;

    if (!type.valuesEndpoint)
        return Promise.reject(new Error(`Codelist type "${type.name}" is missing values endpoint path.`));

    if (typeof valueLoader !== 'function')
        throw new Error(`Codelist type "${type.name}" requires an explicit value loader.`);

    return _codelists.loadOrCreateData(type.valuesEndpoint, () => valueLoader(type), {
        forceReload: forceLoad,
    });

};

// ------------------------------------------------------------------------

function getDefaultFormat(typeId) {

    const config = getConfig();

    switch (typeId) {
        case Types.Int: // int
            return config.intFormat;
        case Types.Float: // float
            return config.floatFormat;
        case Types.Money: // money
            return config.moneyFormat;
        // case Types.Bool: // bool
        //     return null;
        case Types.UtcDatetime: // datetime
            return config.utcDatetimeFormat;
        case Types.ZonedDatetime: // datetime
            return config.zonedDatetimeFormat;
        case Types.LocalDatetime: // datetime
            return config.datetimeFormat;
        case Types.LocalDate: // date
            return config.dateFormat;
        case Types.LocalTime: // time
            return config.timeFormat;
        default:
            return null;
    }

};

function getFormatter(typeId, defaultFormat) {

    const type = getTypeById(typeId);

    if (isCodelistType(type)) {

        return getCodelistFormatter(type);

    } else {

        defaultFormat = defaultFormat || getDefaultFormat(typeId);

        switch (typeId) {
            case Types.Int: // int
            case Types.Float: // float
            case Types.Decimal: // money
                return getNumericFormatter(defaultFormat);
            case Types.Bool: // bool
                return getBoolFormatter(defaultFormat);
            case Types.UtcDatetime:
            case Types.ZonedDatetime:
            case Types.LocalDatetime:
            case Types.LocalDate:
            case Types.LocalTime:
                return getDatetimeFormatter(defaultFormat, typeId);
            default:
                return getStringFormatter(defaultFormat);
        }

    }

};

function getBoolFormatter(defaultFormat) {
    return {
        format: function (value, format) { return dtv.formatBool(value, format || defaultFormat); },
        parse: function (value, format) { return dtv.parseBool(value, format || defaultFormat); }
    };
};


function getNumericFormatter(defaultFormat) {
    return {
        format: function (value, format) { return dtv.formatNumber(value, format || defaultFormat); },
        parse: function (value, format) { return dtv.parseNumber(value, format || defaultFormat); }
    };
};

function getDatetimeFormatter(defaultFormat, typeId = null) {

    function serializeWire(value) {

        switch (typeId) {
            case Types.UtcDatetime:
                return dtv.serializeWireUtcDatetime(value);
            case Types.ZonedDatetime:
                return dtv.serializeWireZonedDatetime(value);
            case Types.LocalDatetime:
                return dtv.serializeWireLocalDatetime(value);
            case Types.LocalDate:
                return dtv.serializeWireLocalDate(value);
            case Types.LocalTime:
                return dtv.serializeWireLocalTime(value);
            default:
                return value;
        }

    }

    function deserializeWire(value) {

        switch (typeId) {
            case Types.UtcDatetime:
                return dtv.parseWireUtcDatetime(value);
            case Types.ZonedDatetime:
                return dtv.parseWireZonedDatetime(value);
            case Types.LocalDatetime:
                return dtv.parseWireLocalDatetime(value);
            case Types.LocalDate:
                return dtv.parseWireLocalDate(value);
            case Types.LocalTime:
                return dtv.parseWireLocalTime(value);
            default:
                return value;
        }

    }

    return {
        format: function (value, format) { return dtv.formatDatetime(value, format || defaultFormat); },
        parse: function (value, format) { return dtv.parseDatetime(value, format || defaultFormat); },
        serializeWire,
        deserializeWire,
    };
};

function getStringFormatter(defaultFormat) {
    return {
        format: function (value, format) { return dtv.formatString(value, format || defaultFormat); },
        parse: function (value, format) { return dtv.parseString(value, format || defaultFormat); }
    };
};

function formatValue(value, typeId, format) {

    let formatter = getFormatter(typeId);
    return formatter.format(value, format);

};

function parseValue(value, typeId, format) {

    let formatter = getFormatter(typeId);
    return formatter.parse(value, format);

};

function getCodelistFormatter(typeOrTypeId, members) {

    const type = typeof typeOrTypeId == 'object'
        ? typeOrTypeId
        : getTypeById(typeOrTypeId);

    if (!members) {

        members = () => {
            return getTypeValues(type);
        };

    }

    return {
        format: function (value) {

            let actualMembers = ((typeof members == 'function') ? members() : members) || [];

            if (!(actualMembers && actualMembers.length))
                getLogger().warning(`Codelist is empty!`)

            let item = actualMembers.find((member) => {
                return member.value == value || member.id == value || member.key == value;
            });

            return (item && (item.label || item.displayLabel || item.name)) || null;
        },
        parse: function (value) {

            let actualMembers = ((typeof members == 'function') ? members() : members) || [];

            if (!(actualMembers && actualMembers.length))
                getLogger().warn(`Codelist is empty!`)

            let item = actualMembers.find((member) => {
                return member.label == value || member.displayLabel == value || member.name == value;
            });

            return (item && (item.value || item.id || item.key)) || null;
        }
    };


};

// ------------------------------------------------------------------------

export {

    Kinds,
    Types,
    RuntimeTypes,
    TypeInfos,

    initialize,

    getTypes,
    getTypeByName,
    getTypeById,

    getTypeKind,
    getTypesByKind,
    isTypeIdOfKind,

    isOfType,
    isBoolType,
    isRefType,
    isIntType,
    isFloatType,
    isNumericType,
    isStringType,
    isUtcDatetimeType,
    isZonedDatetimeType,
    isLocalDatetimeType,
    isLocalDateType,
    isLocalTimeType,
    isDurationType,
    isCodelistType,
    isValueType,

    getTypeCodelistName,
    getTypeValues,

    loadTypeValues,

    getDefaultFormat,

    getBoolFormatter,
    getNumericFormatter,
    getDatetimeFormatter,
    getStringFormatter,
    getCodelistFormatter,

    getFormatter,

    formatValue,
    parseValue,

};

